import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link as RouterLink } from "react-router";
import {
  Archive,
  MessageSquarePlus,
  MoreHorizontal,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import {
  useConversations,
  useDeleteConversation,
  useRenameConversation,
  type Conversation,
} from "@bastion/assistant";
import { useReviewQueue } from "@bastion/data";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ResizeHandle,
} from "@bastion/ui";
import {
  getProductTab,
  isItemActive,
  matchProductGroup,
  type ProductGroupId,
  type ProductNavItem,
} from "../navigation";
import { useArchivedChats } from "./useArchivedChats";
import { useStarredChats } from "./useStarredChats";
import styles from "./AppSecondarySidebar.module.css";

export interface AppSecondarySidebarProps {
  pathname: string;
  /** The open state: the saved toggle, or the phone drawer (AppLayout). */
  isOpen: boolean;
  /**
   * True on a phone, where the sidebar is in the navigation drawer. A link
   * then takes the entry of the drawer, and the width does not change.
   */
  inDrawer?: boolean;
  onNavigate: (path: string) => void;
  onSidebarWidthChange: (px: number) => void;
  onSidebarWidthReset: () => void;
}

function sessionIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/chat\/([^/]+)/);
  return match ? match[1] : null;
}

function sortByUpdatedDesc(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
}

type RecentBucket = {
  id: string;
  label: string;
  conversations: Conversation[];
};

function bucketByRecency(conversations: Conversation[]): RecentBucket[] {
  if (conversations.length === 0) return [];

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOf7Days = startOfToday - 7 * 24 * 60 * 60 * 1000;
  const startOf30Days = startOfToday - 30 * 24 * 60 * 60 * 1000;

  const buckets: Record<string, Conversation[]> = {
    today: [],
    yesterday: [],
    week: [],
    month: [],
    older: [],
  };

  for (const conversation of conversations) {
    const updated = new Date(conversation.updated_at).getTime();
    if (Number.isNaN(updated)) {
      buckets.older.push(conversation);
      continue;
    }
    if (updated >= startOfToday) buckets.today.push(conversation);
    else if (updated >= startOfYesterday) buckets.yesterday.push(conversation);
    else if (updated >= startOf7Days) buckets.week.push(conversation);
    else if (updated >= startOf30Days) buckets.month.push(conversation);
    else buckets.older.push(conversation);
  }

  return [
    { id: "today", label: "Today", conversations: buckets.today },
    { id: "yesterday", label: "Yesterday", conversations: buckets.yesterday },
    { id: "week", label: "Previous 7 days", conversations: buckets.week },
    { id: "month", label: "Previous 30 days", conversations: buckets.month },
    { id: "older", label: "Older", conversations: buckets.older },
  ].filter((bucket) => bucket.conversations.length > 0);
}

function renameErrorMessage(error: unknown): string {
  const status = typeof error === "object" && error !== null && "status" in error
    ? (error as { status?: unknown }).status
    : undefined;
  if (status === 404 || status === 405) {
    return "Rename is not available on this API yet.";
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return "Rename failed. Try again.";
}

function ChatGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section className={styles.chatGroup} aria-label={label}>
      <header className={styles.chatGroupHeader}>{label}</header>
      <div className={styles.chatGroupBody}>{children}</div>
    </section>
  );
}

function ChatSessionRow({
  sessionId,
  conversation,
  isActive,
  isStarred,
  activeSessionId,
  replaceLinks,
  onNavigate,
  onToggleStar,
  onArchive,
  renameConversation,
  deleteConversation,
}: {
  sessionId: string;
  conversation: Conversation | null;
  isActive: boolean;
  isStarred: boolean;
  activeSessionId: string | null;
  replaceLinks: boolean;
  onNavigate: (path: string) => void;
  onToggleStar: (id: string) => void;
  onArchive: (id: string) => void;
  renameConversation: ReturnType<typeof useRenameConversation>;
  deleteConversation: ReturnType<typeof useDeleteConversation>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const renameSubmittedRef = useRef(false);

  const title =
    conversation?.title?.trim() ||
    (sessionId.length >= 8 ? `Chat · ${sessionId.slice(0, 8)}…` : "Chat");

  useEffect(() => {
    if (!renaming) return;
    setDraftTitle(conversation?.title?.trim() || title);
    setRenameError(null);
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(id);
  }, [renaming, conversation?.title, title]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const handleRenameSubmit = useCallback(() => {
    const next = draftTitle.trim();
    if (!next) {
      setRenameError("Title cannot be empty.");
      return;
    }
    if (next === title) {
      setRenaming(false);
      closeMenu();
      return;
    }
    setRenameSubmitting(true);
    setRenameError(null);
    renameSubmittedRef.current = true;
    renameConversation.mutate(
      { sessionId, title: next },
      {
        onSuccess: () => {
          setRenaming(false);
          closeMenu();
        },
        onError: (error) => {
          renameSubmittedRef.current = false;
          setRenameError(renameErrorMessage(error));
          window.requestAnimationFrame(() => inputRef.current?.focus());
        },
        onSettled: () => {
          setRenameSubmitting(false);
        },
      },
    );
  }, [closeMenu, draftTitle, renameConversation, sessionId, title]);

  const handleDelete = useCallback(() => {
    closeMenu();
    if (
      !window.confirm(
        "Delete this conversation? This will permanently remove it and its messages.",
      )
    ) {
      return;
    }
    deleteConversation.mutate(sessionId, {
      onSuccess: () => {
        if (sessionId === activeSessionId) {
          onNavigate("/");
        }
      },
    });
  }, [activeSessionId, closeMenu, deleteConversation, onNavigate, sessionId]);

  return (
    <div
      className={styles.chatRowWrap}
      data-active={isActive ? "true" : "false"}
      data-renaming={renaming ? "true" : "false"}
      data-starred={isStarred ? "true" : "false"}
    >
      {renaming ? (
        <div className={styles.chatRowRename}>
          <input
            ref={inputRef}
            className={styles.chatRowRenameInput}
            value={draftTitle}
            disabled={renameSubmitting}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleRenameSubmit();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                renameSubmittedRef.current = false;
                setRenameError(null);
                setRenaming(false);
              }
            }}
            onBlur={() => {
              queueMicrotask(() => {
                if (renameSubmittedRef.current) {
                  renameSubmittedRef.current = false;
                  return;
                }
                if (draftTitle.trim() && draftTitle.trim() !== title) {
                  handleRenameSubmit();
                  return;
                }
                setRenaming(false);
              });
            }}
            aria-label="Conversation title"
          />
          {renameError ? (
            <p className={styles.chatRowRenameError}>{renameError}</p>
          ) : null}
        </div>
      ) : (
        <>
          <RouterLink
            to={`/chat/${sessionId}`}
            replace={replaceLinks || undefined}
            className={styles.chatRow}
            aria-current={isActive ? "page" : undefined}
            title={title}
          >
            {isStarred ? (
              <Star
                className={styles.chatRowStar}
                aria-hidden="true"
                fill="currentColor"
              />
            ) : null}
            <span className={styles.chatRowTitle}>{title}</span>
          </RouterLink>
          <div className={styles.chatRowTools}>
            <Popover open={menuOpen} onOpenChange={setMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={styles.chatRowKebab}
                  aria-label="Chat actions"
                  data-open={menuOpen ? "true" : "false"}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal
                    className={styles.chatRowKebabIcon}
                    aria-hidden="true"
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="right"
                sideOffset={6}
                collisionPadding={8}
                className={styles.rowMenu}
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <button
                  type="button"
                  className={styles.rowMenuItem}
                  role="menuitem"
                  onClick={() => {
                    onToggleStar(sessionId);
                    closeMenu();
                  }}
                >
                  <Star className={styles.rowMenuIcon} aria-hidden="true" />
                  <span>{isStarred ? "Unstar" : "Star"}</span>
                </button>
                <button
                  type="button"
                  className={styles.rowMenuItem}
                  role="menuitem"
                  onClick={() => {
                    closeMenu();
                    setRenaming(true);
                  }}
                >
                  <span className={styles.rowMenuTextOnly}>Rename</span>
                </button>
                <button
                  type="button"
                  className={styles.rowMenuItem}
                  role="menuitem"
                  onClick={() => {
                    onArchive(sessionId);
                    closeMenu();
                  }}
                >
                  <Archive className={styles.rowMenuIcon} aria-hidden="true" />
                  <span>Archive</span>
                </button>
                <button
                  type="button"
                  className={[styles.rowMenuItem, styles.rowMenuItemDanger].join(" ")}
                  role="menuitem"
                  onClick={handleDelete}
                >
                  <Trash2 className={styles.rowMenuIcon} aria-hidden="true" />
                  <span>Delete</span>
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </>
      )}
    </div>
  );
}

function RecentSkeleton() {
  return (
    <div className={styles.skeletonStack} aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={styles.skeletonRow}>
          <div className={styles.skeletonLineWide} />
          <div className={styles.skeletonLineNarrow} />
        </div>
      ))}
    </div>
  );
}

function secondaryTitle(group: ProductGroupId): string {
  if (group === "settings") return "Settings";
  if (group === "chat") return getProductTab("home")?.label ?? "Home";
  return getProductTab(group)?.label ?? "";
}

function NavLinks({
  items,
  pathname,
  replaceLinks,
}: {
  items: readonly ProductNavItem[];
  pathname: string;
  replaceLinks: boolean;
}) {
  const hasReview = items.some((item) => item.path === "/review");
  const reviewQueue = useReviewQueue(3, { enabled: hasReview });
  return (
    <ul className={styles.navList}>
      {items.map((item) => {
        const Icon = item.icon;
        const active = isItemActive(pathname, item.path);
        return (
          <li key={item.id}>
            <RouterLink
              to={item.path}
              replace={replaceLinks || undefined}
              className={[styles.navItem, active ? styles.navItemActive : ""]
                .filter(Boolean)
                .join(" ")}
              aria-current={active ? "page" : undefined}
            >
              <Icon className={styles.navItemIcon} aria-hidden="true" />
              <span className={styles.navItemLabel}>{item.label}</span>
              {item.path === "/review" &&
              (reviewQueue.data?.pending_count ?? 0) > 0 ? (
                <span className={styles.navBadge}>
                  {reviewQueue.data?.pending_count}
                </span>
              ) : null}
            </RouterLink>
          </li>
        );
      })}
    </ul>
  );
}

// (Per-page "Pinned/Starred views" sidebar section was replaced by the
// dedicated Views menu on each list page. The menu has cross-user
// visibility, per-user default selection, and lives in the page toolbar
// — keeping that as the single entry point avoids duplicating state in
// the sidebar.)

function matchesQuery(conversation: Conversation, query: string): boolean {
  if (!query) return true;
  const haystack = (conversation.title ?? "").toLowerCase();
  return haystack.includes(query);
}

function ChatRecentsBody({
  pathname,
  replaceLinks,
  onNavigate,
}: {
  pathname: string;
  replaceLinks: boolean;
  onNavigate: (path: string) => void;
}) {
  const { data: conversations = [], isLoading } = useConversations();
  const { starredIdSet, toggleStar, isStarred } = useStarredChats();
  const { archivedIdSet, archive } = useArchivedChats();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  const activeSessionId = useMemo(() => sessionIdFromPathname(pathname), [pathname]);
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  const showSearch = !isLoading && conversations.length >= 6;

  const { starredRows, recentBuckets } = useMemo(() => {
    const visible = sortByUpdatedDesc(
      conversations
        .filter((c) => !archivedIdSet.has(c.session_id))
        .filter((c) => matchesQuery(c, trimmedQuery)),
    );
    const starred = visible.filter((c) => starredIdSet.has(c.session_id));
    const recents = visible.filter((c) => !starredIdSet.has(c.session_id));
    return { starredRows: starred, recentBuckets: bucketByRecency(recents) };
  }, [conversations, archivedIdSet, starredIdSet, trimmedQuery]);

  if (isLoading) {
    return <RecentSkeleton />;
  }
  if (conversations.length === 0) {
    return <p className={styles.emptyText}>No conversations yet.</p>;
  }

  const renderRow = (conversation: Conversation) => (
    <ChatSessionRow
      key={conversation.session_id}
      sessionId={conversation.session_id}
      conversation={conversation}
      isActive={conversation.session_id === activeSessionId}
      isStarred={isStarred(conversation.session_id)}
      activeSessionId={activeSessionId}
      replaceLinks={replaceLinks}
      onNavigate={onNavigate}
      onToggleStar={toggleStar}
      onArchive={archive}
      renameConversation={renameConversation}
      deleteConversation={deleteConversation}
    />
  );

  const hasResults = starredRows.length > 0 || recentBuckets.length > 0;

  return (
    <>
      {showSearch ? (
        <div className={styles.search}>
          <Search className={styles.searchIcon} aria-hidden="true" />
          <Input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search chats…"
            aria-label="Search chats"
            className={styles.searchInput}
          />
          {query ? (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <X className={styles.searchClearIcon} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={styles.chatSections}>
        {!hasResults && trimmedQuery ? (
          <p className={styles.sectionEmpty}>
            No matches for “{query.trim()}”.
          </p>
        ) : null}
        {starredRows.length > 0 ? (
          <ChatGroup label="Starred">{starredRows.map(renderRow)}</ChatGroup>
        ) : null}
        {recentBuckets.length === 0 && !trimmedQuery ? (
          <p className={styles.sectionEmpty}>No recent chats.</p>
        ) : null}
        {recentBuckets.map((bucket) => (
          <ChatGroup key={bucket.id} label={bucket.label}>
            {bucket.conversations.map(renderRow)}
          </ChatGroup>
        ))}
      </div>
    </>
  );
}

export function AppSecondarySidebar({
  pathname,
  isOpen,
  inDrawer = false,
  onNavigate,
  onSidebarWidthChange,
  onSidebarWidthReset,
}: AppSecondarySidebarProps) {
  const group = matchProductGroup(pathname);
  const title = secondaryTitle(group);

  const handleNewChat = useCallback(() => {
    onNavigate(`/chat/${crypto.randomUUID()}`);
  }, [onNavigate]);

  const body = (() => {
    if (group === "settings") {
      return <p className={styles.emptyText}>No section navigation on this page.</p>;
    }
    if (group === "chat") {
      return (
        <ChatRecentsBody pathname={pathname} replaceLinks={inDrawer} onNavigate={onNavigate} />
      );
    }
    const tab = getProductTab(group);
    if (!tab || tab.items.length === 0) {
      return <p className={styles.emptyText}>Nothing here yet.</p>;
    }
    return <NavLinks items={tab.items} pathname={pathname} replaceLinks={inDrawer} />;
  })();

  return (
    <aside
      className={styles.secondary}
      data-open={isOpen ? "true" : "false"}
      aria-label={title}
      aria-hidden={!isOpen}
      inert={!isOpen}
    >
      {/* The width comes from --app-secondary-sidebar-inner-width (AppLayout). */}
      <div className={styles.inner}>
        <div className={styles.header}>
          {group === "chat" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={styles.newChatButton}
              onClick={handleNewChat}
              aria-label="New chat"
              title="New chat"
            >
              <MessageSquarePlus className={styles.newChatIcon} aria-hidden="true" />
              <span>New chat</span>
            </Button>
          ) : (
            <span className={styles.title}>{title}</span>
          )}
        </div>
        <div className={styles.body}>{body}</div>
        {isOpen && !inDrawer ? (
          <ResizeHandle
            side="right"
            minWidth={180}
            maxWidth={480}
            onResize={onSidebarWidthChange}
            onResizeEnd={onSidebarWidthChange}
            onDoubleClick={onSidebarWidthReset}
          />
        ) : null}
      </div>
    </aside>
  );
}
